import React from 'react';
import Icon from '../../../components/AppIcon';

const FinancialHealthScore = ({ score, trend, loading = false }) => {
  const getScoreColor = (score) => {
    if (score >= 80) return '#00FF69';
    if (score >= 60) return '#FFB800';
    return '#FF3366';
  };

  const getScoreGradient = (score) => {
    if (score >= 80) return 'from-fintech-green-500 to-fintech-teal-500';
    if (score >= 60) return 'from-yellow-500 to-orange-500';
    return 'from-red-500 to-pink-500';
  };

  const getScoreLabel = (score) => {
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Good';
    if (score >= 40) return 'Fair';
    return 'Needs Attention';
  };

  const circumference = 2 * Math.PI * 70;
  const strokeDashoffset = circumference - (score / 100) * circumference;

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-white via-fintech-blue-50 to-fintech-purple-50 rounded-3xl p-6 md:p-8 shadow-elevation-3 border border-fintech-blue-200 animate-pulse">
        <div className="flex items-start justify-between mb-6">
          <div className="space-y-2">
            <div className="h-6 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded w-48 animate-shimmer" style={{ backgroundSize: '200% 100%' }} />
            <div className="h-4 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded w-64 animate-shimmer" style={{ backgroundSize: '200% 100%' }} />
          </div>
          <div className="w-20 h-8 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded-xl animate-shimmer" style={{ backgroundSize: '200% 100%' }} />
        </div>
        <div className="flex flex-col lg:flex-row items-center gap-8">
          <div className="relative w-48 h-48 md:w-56 md:h-56 rounded-full bg-gradient-to-br from-gray-200 to-gray-300 animate-pulse" />
          <div className="flex-1 w-full space-y-4">
            <div className="h-20 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded-2xl animate-shimmer" style={{ backgroundSize: '200% 100%' }} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="h-20 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded-2xl animate-shimmer" style={{ backgroundSize: '200% 100%' }} />
              <div className="h-20 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded-2xl animate-shimmer" style={{ backgroundSize: '200% 100%' }} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-white via-fintech-blue-50 to-fintech-purple-50 rounded-3xl p-6 md:p-8 shadow-elevation-3 border border-fintech-blue-200 hover:shadow-elevation-4 transition-all duration-300 group">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-lg md:text-xl font-bold text-foreground mb-1 transition-colors duration-200 group-hover:text-primary">Financial Health Score</h2>
          <p className="text-sm text-muted-foreground">Your overall financial wellness rating</p>
        </div>
        <div className={`flex items-center gap-1 px-3 py-1.5 rounded-xl ${trend === 'up' ? 'bg-fintech-green-100 border border-fintech-green-300' : 'bg-red-100 border border-red-300'} shadow-elevation-1 transition-all duration-200 hover:scale-110`}>
          <Icon name={trend === 'up' ? 'TrendingUp' : 'TrendingDown'} size={16} color={trend === 'up' ? '#00AD45' : '#DC2626'} />
          <span className={`text-sm font-bold ${trend === 'up' ? 'text-fintech-green-700' : 'text-red-700'}`}>
            {trend === 'up' ? '+5' : '-3'} pts
          </span>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row items-center gap-8">
        <div className="relative w-48 h-48 md:w-56 md:h-56 transition-transform duration-300 group-hover:scale-105">
          <svg className="transform -rotate-90 w-full h-full">
            <circle
              cx="50%"
              cy="50%"
              r="70"
              stroke="#E2E8F0"
              strokeWidth="12"
              fill="none"
            />
            <circle
              cx="50%"
              cy="50%"
              r="70"
              stroke={getScoreColor(score)}
              strokeWidth="12"
              fill="none"
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              strokeLinecap="round"
              className="transition-all duration-1000 drop-shadow-lg"
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className={`text-5xl md:text-6xl font-bold bg-gradient-to-br ${getScoreGradient(score)} bg-clip-text text-transparent animate-fade-in`}>
              {score}
            </span>
            <span className="text-sm md:text-base text-muted-foreground mt-1 font-medium">out of 100</span>
          </div>
        </div>

        <div className="flex-1 w-full space-y-4">
          <div className="flex items-center justify-between p-4 bg-white/70 backdrop-blur-sm rounded-2xl border border-fintech-blue-200 shadow-elevation-1 hover:shadow-elevation-3 transition-all duration-300 hover:scale-[1.02] cursor-pointer">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${getScoreGradient(score)} flex items-center justify-center shadow-elevation-1 transition-transform duration-300 hover:rotate-12`}>
                <Icon name="TrendingUp" size={20} color="#FFFFFF" />
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground">Status</p>
                <p className="text-xs text-muted-foreground">Current rating</p>
              </div>
            </div>
            <span className={`text-base md:text-lg font-bold bg-gradient-to-br ${getScoreGradient(score)} bg-clip-text text-transparent`}>
              {getScoreLabel(score)}
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="p-4 bg-white/70 backdrop-blur-sm rounded-2xl border border-fintech-purple-200 shadow-elevation-1 hover:shadow-elevation-3 transition-all duration-300 hover:scale-[1.02] cursor-pointer group/card">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="Target" size={16} color="#8700FF" className="transition-transform duration-300 group-hover/card:scale-110" />
                <span className="text-xs text-muted-foreground font-medium">Next Milestone</span>
              </div>
              <p className="text-base md:text-lg font-bold text-fintech-purple-600">80 Points</p>
            </div>
            <div className="p-4 bg-white/70 backdrop-blur-sm rounded-2xl border border-fintech-teal-200 shadow-elevation-1 hover:shadow-elevation-3 transition-all duration-300 hover:scale-[1.02] cursor-pointer group/card">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="Calendar" size={16} color="#00D6AA" className="transition-transform duration-300 group-hover/card:scale-110" />
                <span className="text-xs text-muted-foreground font-medium">Last Updated</span>
              </div>
              <p className="text-base md:text-lg font-bold text-fintech-teal-600">Today</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancialHealthScore;